import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hook/useAuth';
import { upload_CommonFile } from '../../services/commonFileUpload';
import { getMember, put_Member } from '../../services/Member';
import baseURL from '../../config/api';
import FamilyMemberList from './FamilyMemberList';
import DeleteFamilyMember from './DeleteFamilyMember';
import { formatUSPhone, handleUSPhoneChange } from '../../utils/phoneFormatter';
import { useUSPhoneValidation } from '../../hook/useUSPhoneValidation';
import CustomFormatDatePicker from '../../components/CustomFormatDatePicker';
import { getFamilyMember, put_FamilyMember } from '../../services/FamilyMember';
import UsernameValidatedComponent from '../../components/UsernameValidatedComponent';
import PasswordValidation from '../../components/PasswordValidation';
import EmailAlreadyExistChecking from '../../components/EmailAlreadyExistChecking';
import { getFamilyMemberUsername } from '../../services/UsernameChecking';
import { getFamilyMemberEmailChecking } from '../../services/EmailChecking';
import Cookies from 'js-cookie';
import useConfirm from '../../hook/useConfirm';
import AddFamily from './AddFamily';
import LocationFields from '../../components/LocationFields';

const MemberMyAccount = () => {
  const token = Cookies.get("token-my-cdir");
  const navigate = useNavigate();
  const { auth }: any = useAuth();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [userData, setUserData] = useState<any>({
    id: 0,
    userName: "",
    familyName: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    birthday: "",
    imagePath: "",
    street: "",
    street2: "",
    city: "",
    state: "",
    country: "United States",
    zip: "",
  });
  const [password, setPassword] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
  const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
  const {
    isValid: isPhoneValid,
    touched: phoneTouched,
    setTouched: setPhoneTouched
  } = useUSPhoneValidation(userData.phoneNumber);
  const [isUsernameChecking, setIsUsernameChecking] = useState<boolean>(false);
  const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false);

  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState<boolean>(false);

  useEffect(() => {
    if (auth?.id) {
      if (!isNaN(auth?.memberId)) {
        getMember(auth?.memberId, token!.toString())
          .then(res => {
            setUserData({
              ...res,
              birthday: res?.birthday
                ? res.birthday.split("T")[0]
                : "",
              imagePath: res?.imagePath ? `${baseURL}commonFile/Get?name=${res.imagePath}` : "./images/user-default.jpg"
            });
          })
          .catch(error => console.error(error));
      } else {
        getFamilyMember(auth?.id, token!.toString())
          .then(res => {
            setUserData({
              ...res,
              birthday: res?.birthday
                ? res.birthday.split("T")[0]
                : "",
              imagePath: res?.imagePath ? `${baseURL}commonFile/Get?name=${res.imagePath}` : "./images/user-default.jpg"
            });
          })
          .catch(error => console.error(error));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  const inputRef = useRef<HTMLInputElement>(null);
  const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files as FileList;

    if (selectedFiles.length !== 0) {
      const formData = new FormData();
      formData.append("ImageFile", selectedFiles[0]);

      upload_CommonFile(formData, token!.toString())
        .then(res => {
          setUserData({ ...userData, imagePath: `${baseURL}commonFile/Get?name=${res}` });
        })
        .catch(error => {
          console.error(error)
        })
    }
  };

  const closeModal = () => navigate(-1);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isProcessing) {
      setIsProcessing(true);

      if (!isNaN(auth?.memberId)) {
        delete userData?.familyMembers;

        put_Member({
          ...userData,
          imagePath: userData?.imagePath?.replace(`${baseURL}commonFile/Get?name=`, "")
        }, token!.toString())
          .then(() => {
            setIsProcessing(false);
            navigate(-1);
          })
          .catch(error => {
            setIsProcessing(false);
            console.error(error);
          });
      } else {
        if (isUsernameChecking || isEmailChecking) {
          setIsProcessing(false);
          return;
        }

        put_FamilyMember({
          ...userData,
          password,
          imagePath: userData?.imagePath?.replace(`${baseURL}commonFile/Get?name=`, "")
        }, token!.toString())
          .then(() => {
            setIsProcessing(false);
            navigate(-1);
          })
          .catch(error => {
            const errorMessage = error?.response?.data || error?.message || "Something went wrong, Please try again.";
            setConfirmData({
              title: 'Warning',
              summary: errorMessage,
              confirmBtn: 'Ok',
              isConfirm: (callback: any) => {
                callback();
              }
            });
            setConfirmPopup(true);
            setIsProcessing(false);
            console.error(error);
          });
      }
    }
  }

  return (
    <div className="w-full">
      <div className="bg-gradient-to-r from-cyan-300 to-blue-300 w-full h-60 flex flex-col items-center justify-center">
        <img
          src={userData.imagePath}
          alt="member_icon"
          className="w-40 h-40 rounded-full border-[8px] bg-white inline-block z-10 relative cursor-pointer"
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = "/images/user-default.jpg";
          }}
          onClick={() => inputRef?.current?.click()}
        />

        <p
          className="text-sm text-secondary font-medium cursor-pointer hover:underline mt-2 z-20 relative"
          onClick={() => inputRef?.current?.click()}
        >
          Click to update profile picture
        </p>

        <input
          type="file"
          accept="image/png, image/jpeg"
          name="image-choose"
          ref={inputRef}
          className="hidden"
          onChangeCapture={onFileChangeCapture}
        />
      </div>

      <div className="-mt-28 mb-6 px-4">
        <div className="mx-auto mb-12 max-w-6xl shadow-lg py-8 px-6 relative bg-white rounded">
          <div className="h-4 md:h-8" />
          <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 text-secondary">

            {
              isNaN(auth?.memberId) ? (
                <>
                  <div className="col-span-2">
                    <div className="mt-8 p-8 text-center border-2 border-dashed rounded-lg">
                      <h2 className="text-xl font-semibold text-secondary">
                        No Family Added
                      </h2>

                      <p className="text-sm text-gray-500 mt-2 mb-5">
                        You haven't added any family to your account yet.
                      </p>

                      <button
                        type="button"
                        onClick={() => setShowCreateFamilyModal(true)}
                        className="px-6 py-2.5 rounded bg-secondary text-white text-sm font-semibold hover:bg-custom-blue"
                      >
                        + Create Family
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="font-semibold text-sm">First Name</label>
                    <input type='text' placeholder='Enter First Name'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.firstName}
                      onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm">Last Name</label>
                    <input type='text' placeholder='Enter Last Name'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.lastName}
                      onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                      required
                    />
                  </div>
                  <UsernameValidatedComponent
                    userName={userData.userName}
                    setUserName={(value) => setUserData({ ...userData, userName: value })}
                    getUsername={getFamilyMemberUsername}
                    className={{
                      parentDiv: "",
                      label: "font-semibold text-sm",
                      input: "w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary",
                    }}
                    touched={usernameTouched}
                    setTouched={setUsernameTouched}
                    setIsChecking={setIsUsernameChecking}
                    id={userData?.id}
                    required
                  />
                  <div>
                    <PasswordValidation
                      password={password}
                      setPassword={setPassword}
                      isValid={isValidPassword}
                      setIsValid={setIsValidPassword}
                      className={{
                        label: "font-semibold text-sm",
                        div: "w-full text-sm relative",
                        input: "w-full rounded py-2.5 px-4 mt-2.5 border-2 text-sm text-black outline-secondary"
                      }}
                      touched={passwordTouched}
                      setTouched={setPasswordTouched}
                    />
                  </div>
                  <EmailAlreadyExistChecking
                    text={userData.emailAddress}
                    setText={(value) => setUserData({ ...userData, emailAddress: value })}
                    getEmailChecking={getFamilyMemberEmailChecking}
                    className={{
                      parentDiv: "",
                      label: "font-semibold text-sm",
                      input: "w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary",
                    }}
                    touched={emailTouched}
                    setTouched={setEmailTouched}
                    setIsChecking={setIsEmailChecking}
                    id={userData?.id}
                    required
                  />
                  <div>
                    <label className="font-semibold text-sm">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formatUSPhone(userData.phoneNumber)}
                      onChange={(e) => {
                        setPhoneTouched(true);
                        handleUSPhoneChange(e, (digits) =>
                          setUserData((prev: any) => ({
                            ...prev,
                            phoneNumber: digits,
                          }))
                        );
                      }}
                      className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                      placeholder="Phone No."
                    />

                    {!isPhoneValid && phoneTouched && (
                      <p className="text-xs text-red-500 mt-1">
                        Please enter a valid 10-digit phone number
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="font-semibold text-sm">
                      Birthday
                    </label>

                    <CustomFormatDatePicker
                      value={userData.birthday}
                      onChange={(val) =>
                        setUserData((prev: any) => ({
                          ...prev,
                          birthday: val,
                        }))
                      }
                      required={false}
                    />
                  </div>

                  <br className='md:block hidden' />
                </>
              ) : (
                <>
                  <div>
                    <label className="font-semibold text-sm">Family Name</label>
                    <input
                      type="text"
                      placeholder="Enter Family Name"
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.familyName}
                      onChange={(e) => setUserData({ ...userData, familyName: e.target.value })}
                      required
                    />
                  </div>

                  <br className='md:block hidden' />

                  <div>
                    <label className="font-semibold text-sm">First Name</label>
                    <input type='text' placeholder='Enter First Name'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.firstName}
                      onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-sm">Last Name</label>
                    <input type='text' placeholder='Enter Last Name'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.lastName}
                      onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-sm">Email</label>
                    <input type='email' placeholder='Email'
                      className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                      value={userData.emailAddress}
                      onChange={(e) => setUserData({ ...userData, emailAddress: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-sm">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formatUSPhone(userData.phoneNumber)}
                      onChange={(e) => {
                        setPhoneTouched(true);
                        handleUSPhoneChange(e, (digits) =>
                          setUserData((prev: any) => ({
                            ...prev,
                            phoneNumber: digits,
                          }))
                        );
                      }}
                      required
                      className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                      placeholder="Phone No."
                    />

                    {!isPhoneValid && phoneTouched && (
                      <p className="text-xs text-red-500 mt-1">
                        Please enter a valid 10-digit phone number
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="font-semibold text-sm">Street</label>
                    <input type='text' placeholder='Street'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.street}
                      onChange={(e) => setUserData({ ...userData, street: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-sm">Street 2</label>
                    <input type='text' placeholder='Street 2'
                      className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                      value={userData.street2}
                      onChange={(e) => setUserData({ ...userData, street2: e.target.value })}
                    />
                  </div>

                  <LocationFields
                    userData={userData}
                    setUserData={setUserData}
                  />
                </>
              )
            }

            <div className="flex max-xm:flex-col gap-4 !mt-4">
              <button
                type="button"
                className="px-6 py-2 min-w-[150px] rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                onClick={closeModal}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue"
                disabled={isUsernameChecking || isEmailChecking}
              >
                {
                  (isUsernameChecking || isEmailChecking || isProcessing)
                    ?
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18px" fill="#fff" className="mr-2 inline animate-spin"
                        viewBox="0 0 26.349 26.35">
                        <circle cx="13.792" cy="3.082" r="3.082" data-original="#000000" />
                        <circle cx="13.792" cy="24.501" r="1.849" data-original="#000000" />
                        <circle cx="6.219" cy="6.218" r="2.774" data-original="#000000" />
                        <circle cx="21.365" cy="21.363" r="1.541" data-original="#000000" />
                        <circle cx="3.082" cy="13.792" r="2.465" data-original="#000000" />
                        <circle cx="24.501" cy="13.791" r="1.232" data-original="#000000" />
                        <path
                          d="M4.694 19.84a2.155 2.155 0 0 0 0 3.05 2.155 2.155 0 0 0 3.05 0 2.155 2.155 0 0 0 0-3.05 2.146 2.146 0 0 0-3.05 0z"
                          data-original="#000000" />
                        <circle cx="21.364" cy="6.218" r=".924" data-original="#000000" />
                      </svg>
                      Processing...
                    </>
                    :
                    "Save"
                }
              </button>
            </div>
          </form>
        </div>

        {!isNaN(auth?.memberId) && (
          <FamilyMemberList />
        )}

        <DeleteFamilyMember />
      </div>

      <AddFamily
        open={showCreateFamilyModal}
        onClose={() => setShowCreateFamilyModal(false)}
        familyMember={userData}
      />
    </div>
  )
}

export default MemberMyAccount;